import React from 'react';
import { Button } from '@components/atoms/Button';
import { SourceBadge } from '@components/atoms/SourceBadge';
import {
  TaskEquipment,
  TaskEquipmentStatus,
  EquipmentSource,
  MaterialSource,
} from '@models/warehouse.model';
import { Equipment } from '@models/equipment.model';
import { Add } from '@mui/icons-material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ConstructionIcon from '@mui/icons-material/Construction';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import LogoutIcon from '@mui/icons-material/Logout';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EventIcon from '@mui/icons-material/Event';
import { useTranslation } from '@hooks/useTranslation';

interface EquipmentSubTabProps {
  taskEquipment: TaskEquipment[];
  isLoading: boolean;
  isDelegated: boolean;
  onCheckoutEquipment: () => void;
  onReserveEquipment: () => void;
  onReturnEquipment: (equipment: TaskEquipment) => void;
  onCheckoutReserved: (equipment: TaskEquipment) => void;
  onDeleteEquipment: (equipment: TaskEquipment) => void;
  manualEquipment?: Equipment[];
  onAddManually?: () => void;
  onEditManual?: (equipment: Equipment) => void;
  onDeleteManual?: (equipment: Equipment) => void;
}

const formatCurrency = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TaskEquipmentStatusBadge: React.FC<{ status: TaskEquipmentStatus }> = ({ status }) => {
  const tTaskDetail = useTranslation('taskDetail');
  // Sprint 31, story-149 (Instance 2): reads from `enumLabels` now, matching mobile's already-
  // ratified convention (enum display text belongs in `enumLabels`, not a feature-section-local
  // duplicate) — `tTaskDetail.statusUnknown` (the exhaustive-fallback label below) is genuinely
  // feature-local prose, not an enum value, so it stays on `taskDetail`.
  const tEnumLabels = useTranslation('enumLabels');
  const config: Record<TaskEquipmentStatus, { bg: string; text: string; label: string }> = {
    [TaskEquipmentStatus.PLANNED]: {
      bg: 'bg-gray-100',
      text: 'text-gray-700',
      label: tEnumLabels.taskEquipmentStatusPlanned,
    },
    [TaskEquipmentStatus.RESERVED]: {
      bg: 'bg-purple-100',
      text: 'text-purple-700',
      label: tEnumLabels.taskEquipmentStatusReserved,
    },
    [TaskEquipmentStatus.IN_USE]: {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      label: tEnumLabels.taskEquipmentStatusInUse,
    },
    [TaskEquipmentStatus.RETURNED]: {
      bg: 'bg-green-100',
      text: 'text-green-700',
      label: tEnumLabels.taskEquipmentStatusReturned,
    },
  };
  // Sprint 30, story-153. An unrecognized status now surfaces `taskDetail.statusUnknown` rather
  // than being silently mislabeled "Planned" (this `Record<TaskEquipmentStatus, ...>` is
  // already exhaustive, so the fallback only ever fires for a genuinely unexpected value).
  const c = config[status] || {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    label: tTaskDetail.statusUnknown,
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

export const EquipmentSubTab: React.FC<EquipmentSubTabProps> = ({
  taskEquipment,
  isLoading,
  isDelegated,
  onCheckoutEquipment,
  onReserveEquipment,
  onReturnEquipment,
  onCheckoutReserved,
  onDeleteEquipment,
  manualEquipment = [],
  onAddManually,
  onEditManual,
  onDeleteManual,
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  // Counts by status
  const inUseCount = taskEquipment.filter((eq) => eq.status === TaskEquipmentStatus.IN_USE).length;
  const reservedCount = taskEquipment.filter((eq) => eq.status === TaskEquipmentStatus.RESERVED).length;
  const plannedCount = taskEquipment.filter((eq) => eq.status === TaskEquipmentStatus.PLANNED).length;
  const returnedCount = taskEquipment.filter((eq) => eq.status === TaskEquipmentStatus.RETURNED).length;

  // Compute totals (warehouse task equipment)
  const assetTotalCost = taskEquipment.reduce(
    (sum, eq) => sum + (eq.totalCost || 0),
    0,
  );
  // Compute totals (manual equipment)
  const manualTotalCost = manualEquipment.reduce(
    (sum, eq) => sum + (eq.costPerHour || 0) * (eq.amount || 0),
    0,
  );

  // Combined
  const totalCost = assetTotalCost + manualTotalCost;
  const totalItemCount = taskEquipment.length + manualEquipment.length;
  const hasItems = totalItemCount > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{tTaskDetail.equipment.title}</h3>
          {totalItemCount > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">
              {totalItemCount}{' '}
              {totalItemCount !== 1 ? tTaskDetail.equipment.itemPlural : tTaskDetail.equipment.itemSingular}
              {inUseCount > 0 &&
                ` · ${tTaskDetail.equipment.inUseSuffix.replace('{n}', String(inUseCount))}`}
              {reservedCount > 0 &&
                ` · ${tTaskDetail.equipment.reservedSuffix.replace('{n}', String(reservedCount))}`}
              {plannedCount > 0 &&
                ` · ${tTaskDetail.equipment.plannedSuffix.replace('{n}', String(plannedCount))}`}
              {returnedCount > 0 &&
                ` · ${tTaskDetail.equipment.returnedSuffix.replace('{n}', String(returnedCount))}`}
              {manualEquipment.length > 0 &&
                ` · ${tTaskDetail.equipment.manualSuffix.replace('{n}', String(manualEquipment.length))}`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onAddManually && (
            <Button
              leftIcon={<NoteAddIcon sx={{ fontSize: 18 }} />}
              type="button"
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onAddManually();
              }}>
              {tTaskDetail.equipment.addManually}
            </Button>
          )}
          <Button
            leftIcon={<EventIcon />}
            type="button"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onReserveEquipment();
            }}>
            {tTaskDetail.equipment.reserve}
          </Button>
          <Button
            leftIcon={<Add />}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onCheckoutEquipment();
            }}>
            {tTaskDetail.equipment.checkoutFromAssets}
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
        <InfoOutlinedIcon className="text-blue-500 mt-0.5" sx={{ fontSize: 18 }} />
        <p className="text-sm text-blue-700">
          {tTaskDetail.equipment.infoBanner}
        </p>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-400 text-sm">{tTaskDetail.equipment.loadingEquipment}</div>
      ) : hasItems ? (
        <div className="shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.equipment.colSource}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.equipment.colEquipment}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.equipment.colStatus}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.equipment.colEstHours}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.equipment.colCostHr}
                </th>
                {isDelegated && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {tTaskDetail.equipment.colPriceHr}
                  </th>
                )}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.equipment.colTotalCost}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {tTaskDetail.equipment.colActions}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {/* Task Equipment (Warehouse) */}
              {taskEquipment.map((equipment) => (
                <tr
                  key={equipment.taskEquipmentId}
                  className={
                    equipment.status === TaskEquipmentStatus.RETURNED
                      ? 'bg-gray-50 hover:bg-gray-100'
                      : 'hover:bg-gray-50'
                  }>
                  {/* Source */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {equipment.source === EquipmentSource.WAREHOUSE ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                        {tTaskDetail.equipment.badgeAsset}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                        {tTaskDetail.equipment.badgePlanned}
                      </span>
                    )}
                  </td>

                  {/* Equipment Name */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-green-50 rounded flex items-center justify-center flex-shrink-0">
                        <ConstructionIcon className="text-green-500" sx={{ fontSize: 16 }} />
                      </div>
                      <div className="min-w-0 max-w-[250px]">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {equipment.name}
                        </p>
                        {equipment.description && (
                          <p className="text-xs text-gray-500 line-clamp-2 break-words">
                            {equipment.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <TaskEquipmentStatusBadge status={equipment.status} />
                  </td>

                  {/* Est. Hours */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                    {equipment.estimatedHours ? `${equipment.estimatedHours}h` : '—'}
                  </td>

                  {/* Cost/Hr */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                    {equipment.costPerHour ? formatCurrency(equipment.costPerHour) : '—'}
                  </td>

                  {/* Price/Hr (only if delegated) */}
                  {isDelegated && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                      {equipment.pricePerHour ? formatCurrency(equipment.pricePerHour) : '—'}
                    </td>
                  )}

                  {/* Total Cost */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                    {equipment.status === TaskEquipmentStatus.RETURNED ? (
                      <span className={equipment.totalCost > 0 ? 'text-gray-900' : 'text-gray-400'}>
                        {formatCurrency(equipment.totalCost || 0)}
                      </span>
                    ) : (
                      <TaskEquipmentStatusBadge status={equipment.status} />
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center gap-1">
                      {equipment.status === TaskEquipmentStatus.IN_USE && (
                        <button
                          type="button"
                          onClick={() => onReturnEquipment(equipment)}
                          className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded transition-colors"
                          title={tTaskDetail.equipment.returnEquipmentAction}>
                          <KeyboardReturnIcon sx={{ fontSize: 18 }} />
                        </button>
                      )}
                      {equipment.status === TaskEquipmentStatus.RESERVED && (
                        <button
                          type="button"
                          onClick={() => onCheckoutReserved(equipment)}
                          className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                          title={tTaskDetail.equipment.checkoutReservedAction}>
                          <LogoutIcon sx={{ fontSize: 18 }} />
                        </button>
                      )}
                      {(equipment.status === TaskEquipmentStatus.PLANNED ||
                        equipment.status === TaskEquipmentStatus.RESERVED) && (
                        <button
                          type="button"
                          onClick={() => onDeleteEquipment(equipment)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title={tTaskDetail.equipment.removeEquipmentAction}>
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {/* Manual Equipment */}
              {manualEquipment.map((equipment) => (
                <tr key={`manual-${equipment.equipmentId}`} className="hover:bg-gray-50">
                  {/* Source */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <SourceBadge source={MaterialSource.MANUAL} size="sm" />
                  </td>

                  {/* Equipment Name */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                        <NoteAddIcon className="text-gray-400" sx={{ fontSize: 16 }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {equipment.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {equipment.supplierOwner || ''}
                          {equipment.amount
                            ? ` · ${equipment.amount} ${tTaskDetail.equipment.units}`
                            : ''}
                          {equipment.hoursUsed
                            ? ` · ${equipment.hoursUsed} ${tTaskDetail.equipment.hrs}`
                            : ''}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <span className="text-sm text-gray-400">—</span>
                  </td>

                  {/* Est. Hours */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                    {equipment.hoursUsed ? `${equipment.hoursUsed}h` : '—'}
                  </td>

                  {/* Cost/Hr */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                    {equipment.costPerHour ? formatCurrency(equipment.costPerHour) : '—'}
                  </td>

                  {/* Price/Hr (only if delegated) */}
                  {isDelegated && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                      {equipment.chargeToClient && equipment.pricePerHour
                        ? formatCurrency(equipment.pricePerHour)
                        : '—'}
                    </td>
                  )}

                  {/* Total Cost */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                    {equipment.costPerHour && equipment.amount
                      ? formatCurrency(equipment.costPerHour * equipment.amount)
                      : '—'}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center gap-1">
                      {onEditManual && (
                        <button
                          type="button"
                          onClick={() => onEditManual(equipment)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title={tTaskDetail.equipment.editEquipmentAction}>
                          <EditOutlinedIcon sx={{ fontSize: 18 }} />
                        </button>
                      )}
                      {onDeleteManual && (
                        <button
                          type="button"
                          onClick={() => onDeleteManual(equipment)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title={tTaskDetail.equipment.removeEquipmentAction}>
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t border-gray-200">
              <tr>
                <td
                  colSpan={isDelegated ? 6 : 5}
                  className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">
                  {tTaskDetail.equipment.totals}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                  {totalCost > 0 ? formatCurrency(totalCost) : '—'}
                </td>
                <td className="px-4 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <ConstructionIcon className="text-gray-400" sx={{ fontSize: 24 }} />
          </div>
          <p className="text-sm">{tTaskDetail.equipment.noEquipmentAdded}</p>
        </div>
      )}

      {/* Legends */}
      {hasItems && (
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
          {taskEquipment.length > 0 && (
            <>
              <div className="flex items-center gap-1.5">
                <TaskEquipmentStatusBadge status={TaskEquipmentStatus.IN_USE} />
                <span>{tTaskDetail.equipment.legendInUse}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <TaskEquipmentStatusBadge status={TaskEquipmentStatus.RESERVED} />
                <span>{tTaskDetail.equipment.legendReserved}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <TaskEquipmentStatusBadge status={TaskEquipmentStatus.RETURNED} />
                <span>{tTaskDetail.equipment.legendReturned}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <TaskEquipmentStatusBadge status={TaskEquipmentStatus.PLANNED} />
                <span>{tTaskDetail.equipment.legendPlanned}</span>
              </div>
            </>
          )}
          {manualEquipment.length > 0 && (
            <div className="flex items-center gap-1.5">
              <SourceBadge source={MaterialSource.MANUAL} size="sm" />
              <span>{tTaskDetail.equipment.legendManual}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
